import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Info, Plus } from 'lucide-react';
import Badge from '../../../../ui/Badge';
import CardOverlays from '../../../../ui/CardOverlays';
import CardViewer from '../../../../ui/CardViewer';
import CardDetail from '../../../../components/CardDetail';
import { useBackLayerManager } from '../../../../ui/backLayer/backLayerContext';
import { formatPrice } from '../../utils/formatPrice';

/**
 * AddResultsViewer — `CardViewer` über den Trefferlisten des mobilen Hinzufügen-Modus
 * (Suche und Vorschläge) mit Aktionszeile „Hinzufügen", Preis, Badges und „Details". Besitzt
 * die Blätter-Position; die Ebene `'add-viewer'` (`useHistoryLayer`) hält der Reiter, weil sein
 * Antippen der Kachel sie öffnet.
 *
 * „Details" öffnet das Karten-Modal ohne Aktion (`canAct={false}`), wie der Desktop-Katalog:
 * die Karte liegt noch nicht im Deck, „Print wechseln" hätte kein Ziel.
 *
 * @param {boolean} isOpen — Ebene `'add-viewer'` ist offen
 * @param {number} [payloadIndex] — Öffnen-Position aus dem Ebenen-Payload
 * @param {() => void} onClose — schließt die Ebene (`close` des History-Hooks)
 * @param {object[]} entries — Trefferliste des Reiters; der Viewer blättert darüber
 * @param {string} status — Lade-Status der Liste: nur `'success'` mit leerer Liste schließt
 *   den Viewer, während des Ladens ist die Liste leer, ohne dass sie es bleibt
 * @param {boolean} canAdd — Knopf „Hinzufügen" zeigen
 * @param {(entry) => void} onAdd — Hinzufügen der gezeigten Karte
 * @param {Set<string>} deckOracleIds — Oracle-IDs des Decks für das Badge „✓ im Deck"
 */
export default function AddResultsViewer({
  isOpen,
  payloadIndex,
  onClose,
  entries,
  status,
  canAdd,
  onAdd,
  deckOracleIds,
}) {
  const { closeLayersDownTo } = useBackLayerManager();
  const [viewerIndex, setViewerIndex] = useState(0);
  const [detailEntry, setDetailEntry] = useState(null);

  const viewerCards = useMemo(
    () => entries.map((entry) => ({
      name: entry.name,
      imageUrl: entry.imageUrl,
      imageUrlBack: entry.imageUrlBack || undefined,
      imageOrientation: entry.imageOrientation,
    })),
    [entries]
  );
  const viewerEntry = entries[Math.min(viewerIndex, entries.length - 1)] || null;

  useEffect(() => {
    if (isOpen) setViewerIndex(Number(payloadIndex) || 0);
  }, [isOpen, payloadIndex]);

  // Ein Filterwechsel bei offenem Viewer verkürzt die Liste unter ihm: Index klemmen,
  // bei fertig geladener leerer Liste die Ebene schließen. Der Ref-Wächter verhindert ein
  // zweites navigate(-1), solange der History-Pop noch nicht angekommen ist.
  const viewerClosingRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      viewerClosingRef.current = false;
      return;
    }
    if (entries.length === 0) {
      if (status === 'success' && !viewerClosingRef.current) {
        viewerClosingRef.current = true;
        closeLayersDownTo('add-viewer');
      }
      return;
    }
    if (viewerIndex > entries.length - 1) setViewerIndex(entries.length - 1);
  }, [isOpen, closeLayersDownTo, entries.length, status, viewerIndex]);

  return (
    <>
      <CardViewer
        open={isOpen}
        cards={viewerCards}
        index={viewerIndex}
        onIndexChange={setViewerIndex}
        onClose={onClose}
        label={`${Math.min(viewerIndex, Math.max(entries.length - 1, 0)) + 1} von ${entries.length}`}
      >
        {viewerEntry && (
          <div className="mobile-add-viewer-actions">
            {canAdd && (
              <button
                type="button"
                className="btn-secondary mobile-add-viewer-actions__add"
                onClick={() => onAdd(viewerEntry)}
              >
                <Plus size={16} aria-hidden /> Hinzufügen
              </button>
            )}
            <span className="mobile-add-viewer-actions__price">{formatPrice(viewerEntry.price)}</span>
            {viewerEntry.ownedQuantity > 0 && <Badge tone="accent">im Besitz</Badge>}
            {viewerEntry.oracleId && deckOracleIds.has(viewerEntry.oracleId) && <Badge>✓ im Deck</Badge>}
            {viewerEntry.oracleId && (
              <CardOverlays.Button icon={Info} label="Details" onClick={() => setDetailEntry(viewerEntry)} />
            )}
          </div>
        )}
      </CardViewer>

      <CardDetail
        open={Boolean(detailEntry)}
        onOpenChange={(next) => { if (!next) setDetailEntry(null); }}
        oracleId={detailEntry?.oracleId}
        scryfallId={detailEntry?.id}
        fallback={detailEntry ? {
          oracleId: detailEntry.oracleId,
          name: detailEntry.name,
          typeLine: detailEntry.typeLine,
          imageUrl: detailEntry.imageUrl,
          imageUrlBack: detailEntry.imageUrlBack || undefined,
          imageOrientation: detailEntry.imageOrientation,
        } : null}
        section="card"
        canAct={false}
        thumbnailOpensViewer={false}
      />
    </>
  );
}
